import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, Edit2, Save, Trash2 } from "lucide-react";
import type { StickyNote } from "@/types/collaboration";

interface StickyNoteComponentProps {
  note: StickyNote;
  onUpdate?: (noteId: string, content: string) => Promise<void>;
  onDelete?: (noteId: string) => Promise<void>;
  canEdit: boolean;
  currentUserId: string;
}

export const StickyNoteComponent = ({
  note,
  onUpdate,
  onDelete,
  canEdit,
  currentUserId,
}: StickyNoteComponentProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(note.content);
  const [isLoading, setIsLoading] = useState(false);

  const isAuthor = note.author_id === currentUserId;
  const canModify = canEdit && isAuthor;

  const handleSave = async () => {
    if (!onUpdate || !editedContent.trim()) return;
    
    setIsLoading(true);
    try {
      await onUpdate(note.id, editedContent);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update note:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    
    if (confirm("Are you sure you want to delete this note?")) {
      setIsLoading(true);
      try {
        await onDelete(note.id);
      } catch (error) {
        console.error("Failed to delete note:", error);
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setEditedContent(note.content);
    setIsEditing(false);
  };

  return (
    <Card className="p-3 bg-yellow-50 border-yellow-200 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <p className="text-xs font-semibold text-gray-700">{note.author_name}</p>
          <p className="text-[10px] text-gray-500">
            {new Date(note.created_at).toLocaleDateString()} at{" "}
            {new Date(note.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        
        {canModify && !isEditing && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleDelete}
              disabled={isLoading}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      {isEditing ? (
        <div className="space-y-2">
          <Textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className="min-h-[80px] text-sm bg-white"
            placeholder="Add your comment..."
            disabled={isLoading}
          />
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isLoading}
            >
              <X className="h-3 w-3 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isLoading || !editedContent.trim()}
            >
              <Save className="h-3 w-3 mr-1" />
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{note.content}</p>
      )}
    </Card>
  );
};